import React, { useEffect, useState } from 'react';
import { pmApi } from '../../lib/pmApi';
import { Plus, CheckCircle2, Clock, XCircle } from 'lucide-react';

const empty = {
  property_name: '', address: '', city: '', state: '', zip: '', unit_count: 0,
  main_contact_name: '', main_contact_email: '', main_contact_phone: '',
  maintenance_contact_name: '', maintenance_contact_phone: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  notes: '',
};

const STATUS_STYLE = {
  Pending:  { icon: Clock,        cls: 'bg-[#D4AF37]/15 text-[#D4AF37]' },
  Approved: { icon: CheckCircle2, cls: 'bg-[#10B981]/15 text-[#10B981]' },
  Rejected: { icon: XCircle,      cls: 'bg-[#EF4444]/15 text-[#EF4444]' },
};

const PropertyRequests = () => {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const load = () => pmApi.listPropertyRequests().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_name.trim()) { setErr('Property name is required'); return; }
    setSubmitting(true); setErr('');
    try {
      await pmApi.submitPropertyRequest({ ...form, unit_count: Number(form.unit_count) || 0 });
      setForm(empty); setShowForm(false); load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="pm-property-requests-page" className="p-6 max-w-4xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY REQUESTS</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Request a New Property</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Submit a new property under your company. NexFlow will review and approve, then you'll gain access automatically.</p>
        </div>
        <button data-testid="pm-pr-new-btn" onClick={() => setShowForm(!showForm)} className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 rounded font-medium hover:bg-[#F3E5AB] inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> {showForm ? 'Hide form' : 'New Request'}
        </button>
      </div>

      {showForm && (
        <form data-testid="pm-pr-form" onSubmit={submit} className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
          {err && <p className="text-[#EF4444] text-sm">{err}</p>}
          <input data-testid="pm-pr-name" required placeholder="Property name *" value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
          <input placeholder="Street address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
            <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
            <input placeholder="Zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
          </div>
          <label className="block">
            <span className="text-[#A3A3A3] text-xs">Unit count</span>
            <input type="number" min="0" value={form.unit_count} onChange={(e) => setForm({ ...form, unit_count: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
          </label>

          <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] pt-2">CONTACTS</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <input placeholder="Main contact name" value={form.main_contact_name} onChange={(e) => setForm({ ...form, main_contact_name: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
            <input placeholder="Main contact phone" value={form.main_contact_phone} onChange={(e) => setForm({ ...form, main_contact_phone: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
            <input placeholder="Main contact email" value={form.main_contact_email} onChange={(e) => setForm({ ...form, main_contact_email: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
            <div />
            <input placeholder="Maintenance contact name" value={form.maintenance_contact_name} onChange={(e) => setForm({ ...form, maintenance_contact_name: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
            <input placeholder="Maintenance contact phone" value={form.maintenance_contact_phone} onChange={(e) => setForm({ ...form, maintenance_contact_phone: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
            <input placeholder="Emergency contact name" value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
            <input placeholder="Emergency contact phone" value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-11 px-3 text-white text-base" />
          </div>
          <textarea placeholder="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white text-base" />
          <button data-testid="pm-pr-submit" disabled={submitting} className="w-full bg-[#D4AF37] text-[#0A0A0A] h-12 rounded font-medium hover:bg-[#F3E5AB] disabled:opacity-40">{submitting ? 'Submitting…' : 'Submit Property Request'}</button>
        </form>
      )}

      <div>
        <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-3">YOUR REQUESTS</p>
        <div className="space-y-2">
          {list.map((r) => {
            const s = STATUS_STYLE[r.status] || STATUS_STYLE.Pending;
            const Icon = s.icon;
            return (
              <div key={r.id} data-testid={`pm-pr-row-${r.id}`} className="bg-[#141414] border border-[#262626] rounded-md p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-medium">{r.property_name}</p>
                    <p className="text-[#A3A3A3] text-xs">{r.address}{r.city ? `, ${r.city}` : ''} · {r.unit_count || 0} units</p>
                    <p className="text-[#525252] text-xs mt-1">Submitted {new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded inline-flex items-center gap-1 ${s.cls}`}><Icon className="w-3.5 h-3.5" /> {r.status}</span>
                </div>
                {r.status === 'Rejected' && r.rejection_reason && (
                  <div data-testid={`pm-pr-rejected-${r.id}`} className="mt-3 bg-[#7F1D1D]/30 border border-[#EF4444]/40 rounded p-3 text-sm">
                    <p className="text-[#FCA5A5] font-medium text-xs uppercase tracking-wider">Rejection reason</p>
                    <p className="text-white mt-1">{r.rejection_reason}</p>
                  </div>
                )}
                {r.status === 'Approved' && r.approved_property_id && (
                  <p className="mt-2 text-[#10B981] text-xs">✓ Live now — visit Properties to manage it.</p>
                )}
              </div>
            );
          })}
          {list.length === 0 && <p className="text-[#525252] text-sm">No property requests yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default PropertyRequests;
